import { useMergedRef } from '@mantine/hooks';
import clsx from 'clsx';
import { useEffect, useId, useRef } from 'react';

export interface CheckboxProps extends Pick<
  React.ComponentPropsWithRef<'input'>,
  'disabled' | 'id' | 'name' | 'onBlur' | 'onChange' | 'onClick' | 'ref' | 'value'
> {
  checked?: boolean | 'indeterminate';
  description?: React.ReactNode;
  label?: React.ReactNode;
}

export function Checkbox(props: CheckboxProps) {
  const {
    checked,
    description,
    disabled,
    id,
    label,
    name,
    onClick,
    value,
    ref: externalRef,
    ...otherProps
  } = props;

  const internalRef = useRef<HTMLInputElement>(null);

  const genId = useId();
  const checkboxId = id || genId;

  let isChecked: boolean | undefined;
  if (typeof checked === 'boolean') {
    isChecked = checked;
  } else if (checked === 'indeterminate') {
    isChecked = false;
  }

  useEffect(() => {
    if (internalRef.current) {
      internalRef.current.indeterminate = checked === 'indeterminate';
    }
  }, [checked]);

  return (
    <div className="flex" onClick={onClick} role="presentation">
      <div className="flex h-5 shrink-0 items-center">
        <div className="group grid size-4 grid-cols-1">
          <input
            {...otherProps}
            ref={useMergedRef(externalRef, internalRef)}
            checked={isChecked}
            className={clsx(
              'col-start-1 row-start-1 cursor-pointer appearance-none rounded-sm border border-gray-300 bg-white checked:border-primary-600 checked:bg-primary-600 indeterminate:border-primary-600 indeterminate:bg-primary-600 focus:ring-primary-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto'
            )}
            disabled={disabled}
            id={checkboxId}
            name={name}
            suppressHydrationWarning
            type="checkbox"
            value={value}
          />
          <svg
            fill="none"
            viewBox="0 0 14 14"
            className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25"
          >
            <path
              d="M3 8L6 11L11 3.5"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="opacity-0 group-has-checked:opacity-100"
            />
            <path
              d="M3 7H11"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="opacity-0 group-has-indeterminate:opacity-100"
            />
          </svg>
        </div>
      </div>
      {label || description ? (
        <div className="ml-3 text-sm">
          {label && (
            <label
              className={clsx('cursor-pointer text-gray-700', description && 'font-medium')}
              htmlFor={checkboxId}
              suppressHydrationWarning
            >
              {label}
            </label>
          )}
          {description && <p className="text-gray-500">{description}</p>}
        </div>
      ) : undefined}
    </div>
  );
}
